import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { logError } from 'utils/logger';
import { BaseSelect } from '../../../../shared/components';
import { listPocContacts, toFormContact, type PocType } from '../../../services/poc.service';

/**
 * Picks an existing contact out of the `/api/poc/*` master for this section.
 *
 * The job create endpoint links contacts by id, so every contact has to exist as a
 * master row. Choosing one here fills the section and records its id; leaving it on
 * "Add new contact" keeps whatever was typed and creates the row on submit.
 */
export default function PocDirectorySelect({
  type,
  path,
  idPath,
  label = 'Existing contact',
  applyRecord,
}: {
  type: PocType;
  path: string;
  idPath?: string;
  label?: string;
  applyRecord?: (record: Record<string, any>) => void;
}) {
  const { watch, setValue } = useFormContext();
  const [contacts, setContacts] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const selectedIdPath = idPath ?? `${path}.id`;
  const selectedId = watch(selectedIdPath) ?? '';

  useEffect(() => {
    let cancelled = false;

    (async () => {
      setLoading(true);
      try {
        const rows = await listPocContacts(type);
        if (!cancelled) setContacts(Array.isArray(rows) ? rows : []);
      } catch (error) {
        // A directory that will not load must not block the step — the analyst can
        // still type the contact out and it gets created on submit.
        logError({ message: `Failed to load ${type} contacts`, error, code: 'POC_LIST_FAILED' });
        if (!cancelled) setContacts([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [type]);

  const options = [
    { label: 'Add new contact', value: '' },
    ...contacts.map((c) => ({
      label: [c.name, c.mobile || c.phone || c.contact].filter(Boolean).join(' — ') || `#${c.id}`,
      value: c.id,
    })),
  ];

  const handleChange = (value: any) => {
    if (value === '' || value == null) {
      // Keep the details on screen; they now describe a contact to be created.
      setValue(selectedIdPath, null, { shouldDirty: true });
      return;
    }

    const record = contacts.find((c) => String(c.id) === String(value));
    if (!record) return;

    if (applyRecord) {
      applyRecord(record);
      return;
    }

    const current = watch(path) ?? {};
    setValue(
      path,
      { ...current, ...toFormContact(type, record) },
      { shouldDirty: true, shouldValidate: false }
    );
  };

  return (
    <BaseSelect
      id={`${selectedIdPath}-directory`}
      label={label}
      value={selectedId ?? ''}
      onChange={handleChange}
      options={options}
      placeholder={loading ? 'Loading contacts…' : 'Add new contact'}
      disabled={loading}
      orientation='row'
      labelVariant='secondary'
    />
  );
}
